import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import dayjs from 'dayjs';
import { importBillingRecords } from '../../../../services/billingRecords.service';

const ImportBillingRecordsModal = ({ open, onClose, onImportSuccess }) => {
  const [importMonth, setImportMonth] = useState(dayjs());
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');

  const formatFileSize = (bytes) => {
    if (bytes === undefined || bytes === null) return '';
    if (bytes === 0) return '0 bytes';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = bytes/Math.pow(k, i);
    return `${value.toFixed(value < 10 && i > 0 ? 1 : 0)} ${sizes[i]}`;
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const fileType = file.name.split('.').pop().toLowerCase();
      if (!['csv', 'xlsx', 'xls'].includes(fileType)) {
        setImportError('Please select a valid CSV or Excel file');
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setImportError('');
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      setImportError('Please select a file to import');
      return;
    }

    if (!importMonth) {
      setImportError('Please select a month');
      return;
    }

    setImporting(true);
    setImportError('');
    setImportSuccess('');

    try {
      const monthStr = importMonth.format('MM/YYYY');
      const result = await importBillingRecords(selectedFile, monthStr);

      if (result.success) {
        const { created, skipped, errors } = result.data;
        let successMsg = `Successfully imported ${created} billing record(s).`;
        if (skipped > 0) {
          successMsg += ` Skipped ${skipped} row(s).`;
        }
        if (errors && errors.length > 0) {
          successMsg += ` ${errors.length} error(s) occurred.`;
        }
        setImportSuccess(successMsg);
        
        if (onImportSuccess) {
          onImportSuccess(result.data);
        }

        // Reset form after 2 seconds
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        setImportError(result.message || 'Failed to import billing records');
      }
    } catch (error) {
      console.error('Import error:', error);
      setImportError(error.message || 'An error occurred during import');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setImportMonth(dayjs());
    setSelectedFile(null);
    setImportError('');
    setImportSuccess('');
    setImporting(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}   
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      >
      <DialogTitle>Import Billing Records</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Select Month"
              views={['month', 'year']}
              value={importMonth}
              onChange={(newValue) => setImportMonth(newValue)}
              format="MM/YYYY"
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'outlined',
                },
              }}
            />
          </LocalizationProvider>

          <Box>
            <input
              accept=".csv,.xlsx,.xls"
              style={{ display: 'none' }}
              id="billing-file-upload"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="billing-file-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<FileUploadIcon />}
                fullWidth
                sx={{ textTransform: 'none' }}
              >
                Select Excel/CSV File
              </Button>
            </label>
            {selectedFile && (
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                Selected: {selectedFile.name}, {formatFileSize(selectedFile.size)}
              </Typography>
            )}
          </Box>

          {importError && (
            <Alert severity="error" onClose={() => setImportError('')}>
              {importError}
            </Alert>
          )}

          {importSuccess && (
            <Alert severity="success" onClose={() => setImportSuccess('')}>
              {importSuccess}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={importing}>
          Cancel
        </Button>
        <Button
          onClick={handleImport}
          variant="contained"
          disabled={importing || !selectedFile}
          startIcon={importing && <CircularProgress size={16} />}
        >
          {importing ? 'Importing...' : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportBillingRecordsModal;
